import React from 'react';
import { Button } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ORDER_GUIDE_STEP_COUNT, type OrderGuideItem, type OrderGuideStepKey } from './orderGuideRules';
import { getOrderProcessStepIndex } from '../orderProcessSteps';
import './OrderGuide.css';

export interface OrderStepGuideAction {
  labelKey: string;
  disabled?: boolean;
  busy?: boolean;
  onClick: () => void;
}

export interface OrderStepGuideProps {
  step: OrderGuideStepKey;
  helpKey: string;
  /** Checklist; null on a finished step shown read-only (title and help only). */
  items: OrderGuideItem[] | null;
  /** Main action of the step (a status move, „Klient potwierdził”, „Stwórz ofertę”). */
  primary?: OrderStepGuideAction | null;
  /** Opens „Ceny sprzedaży” — shown before the main action while a price is missing. */
  onOpenPrices?: () => void;
  /** Step buttons before „Uzupełnij ceny” (Zapisz szkic, Wyślij potwierdzenie do klienta). */
  secondary?: React.ReactNode;
  /** One line under the buttons: why the main action waits, or the warning of the step. */
  hint?: { tone: 'info' | 'warning'; text: string } | null;
}

const ICON: Record<OrderGuideItem['state'], string> = { ok: '✓', todo: '○', info: 'i' };

/**
 * Step card of the first mockup (`stepc`): „Krok N z 8”, the title, the help
 * text, the checklist (✓ done, ○ to do, i information) and the buttons —
 * the main one last, as „X →”. Presentational: the page decides what the rows
 * and the buttons are (`orderStepGuide.ts`, `orderStepBar.ts`).
 */
export default function OrderStepGuide({ step, helpKey, items, primary, onOpenPrices, secondary, hint }: OrderStepGuideProps) {
  const { t } = useTranslation(['orders']);
  const pricesPending = Boolean(items?.some((item) => item.action === 'prices' && item.state !== 'ok'));
  const hasActions = Boolean(primary || secondary || (pricesPending && onOpenPrices));
  return (
    <div className="zk-guide__step" data-testid={`zk-guide-${step}`}>
      <div className="zk-guide__eyebrow">
        {t('orders:process.guide.eyebrow', { step: getOrderProcessStepIndex(step) + 1, total: ORDER_GUIDE_STEP_COUNT })}
      </div>
      <h3 className="zk-guide__title">{t(`orders:process.steps.${step}.label`)}</h3>
      <p className="zk-guide__help">{t(helpKey)}</p>
      {items && (
        <ul className="zk-checks" data-testid="zk-guide-checks">
          {items.map((item) => (
            <li className={`zk-check zk-check--${item.state}`} key={item.id} data-state={item.state} data-testid={`zk-guide-item-${item.id}`}>
              <span className="zk-check__icon" aria-hidden="true">{ICON[item.state]}</span>
              <span className="zk-check__text">
                <span>
                  <span className="zk-visually-hidden">{t(`orders:process.guide.state.${item.state}`)}: </span>
                  {t(item.text.key, item.text.params)}
                </span>
                {item.state === 'todo' && item.hint && <small>{t(item.hint.key, item.hint.params)}</small>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {items && hasActions && (
        <div className="zk-guide__actions" data-testid="zk-action-bar" role="group" aria-label={t('orders:process.bar.ariaLabel')}>
          {secondary}
          {pricesPending && onOpenPrices && (
            <Button onClick={onOpenPrices} data-testid="zk-guide-prices">
              {t('orders:process.guide.actions.prices')}
            </Button>
          )}
          {primary && (
            <Button
              type="primary"
              loading={primary.busy}
              disabled={primary.disabled}
              onClick={primary.onClick}
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              data-testid="zk-primary-action"
            >
              {t(primary.labelKey)}
            </Button>
          )}
        </div>
      )}
      {items && hint && (
        <p className={`zk-guide__foot${hint.tone === 'warning' ? ' zk-guide__foot--warning' : ''}`} data-testid="zk-action-hint" data-tone={hint.tone}>
          {hint.text}
        </p>
      )}
    </div>
  );
}
